import { getSeparator } from "@components/Byline/helpers";
import ArticleBylineImage from "@pages/Article/components/ArticleByline/ArticleBylineImage";
import ArticleBylineName from "@pages/Article/components/ArticleByline/ArticleBylineName";
import ArticleTimeStamp from "@pages/Article/components/ArticleTimeStamp";
import { FeedAuthor } from "@pages/Article/utils/types";
import { cn } from "@util/helpers";

type ArticleBylineProps = {
  /** List of authors for the article */
  authors: FeedAuthor[];
  /** Published date of the article */
  updatedTime: string;
  /** Updated date of the article */
  editedTime?: string;
  /** Flag to indicate if the updated time should be displayed */
  hasUpdatedTimeDisplayed?: boolean;
  /** Optional URL parameters for the author links */
  linkParams?: URLSearchParams;
  /** Optional backdrop class for styling */
  backdrop?: string;
};

/**
 * ArticleByline component that displays the byline information of an article,
 * including authors, published date, and updated date.
 */
const ArticleByline = ({
  authors,
  updatedTime,
  editedTime,
  hasUpdatedTimeDisplayed,
  linkParams,
  backdrop,
}: ArticleBylineProps): React.ReactElement => {
  const authorsCount = authors.length;

  const authorWithoutByline = authors.find((authors) => {
    return !authors?.image;
  });

  if (authorsCount === 0) {
    return (
      <ArticleTimeStamp
        updatedTime={updatedTime}
        editedTime={editedTime || ""}
        hasUpdatedTimeDisplayed={hasUpdatedTimeDisplayed}
      />
    );
  }

  return (
    <div
      className="flex flex-row items-center justify-start gap-2"
      data-testid="article-byline-component"
    >
      {authorsCount >= 1 && authorsCount <= 3 && !authorWithoutByline ? (
        <div className="flex flex-shrink-0 flex-row items-center justify-start">
          {authors?.map((author, index) => {
            return (
              <ArticleBylineImage
                key={author.id}
                author={author}
                className={cn(
                  "aspect-square overflow-hidden rounded-full",
                  {
                    "-translate-x-2": index !== 0,
                  },
                  backdrop
                )}
              />
            );
          })}
        </div>
      ) : null}

      <div
        className={cn("flex flex-col flex-wrap", {
          "pl-2": !authorWithoutByline,
        })}
      >
        <div className="flex flex-wrap gap-x-1.5">
          {authors?.map((author, index) => {
            return (
              <ArticleBylineName
                key={author.id}
                author={author}
                separator={getSeparator(index, authorsCount)}
                className={cn(
                  "word-break align-middle font-poppins text-base font-semibold text-gray-850"
                )}
                linkParams={linkParams}
              />
            );
          })}
        </div>

        <ArticleTimeStamp
          updatedTime={updatedTime}
          editedTime={editedTime || ""}
          hasUpdatedTimeDisplayed={hasUpdatedTimeDisplayed}
        />
      </div>
    </div>
  );
};

export default ArticleByline;
